import React from "react"

import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FileDropZone,
  FormField,
  GroupLabel,
  Input,
  Slider,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@coval/ui"
import { Archive, Check, FileAudio, RotateCcw, Upload } from "lucide-react"

import type { BackgroundSoundResource } from "@/types_api/backend/transport/list_background_sounds_response"
import type { VoicePersonaConfig } from "@/types/ModelConfigs/VoiceModelConfigs"

import type { PersonaFormController } from "../hooks/usePersonaFormController"
import { AudioPreviewButton } from "./AudioPreviewButton"
import { OptionCard } from "./PersonaOptionPickerDialog"

const BACKGROUND_SOUND_UPLOAD_ACCEPT =
  "audio/mpeg,audio/wav,audio/x-wav,.mp3,.wav"
const BACKCHANNELING_EXPLANATION =
  "Adds short listener cues like mm-hmm and yeah to simulate an engaged caller."
const ACOUSTIC_SOURCE_TYPE_UNSET_VALUE = "__unset__"

type AcousticSourceType = "ambient" | "point_source"
type BackgroundSoundWithAcousticSource = BackgroundSoundResource & {
  acoustic_source_type?: AcousticSourceType | null
}

const ACOUSTIC_SOURCE_TYPE_OPTIONS: Array<{
  value: AcousticSourceType
  label: string
}> = [
  { value: "ambient", label: "Ambient" },
  { value: "point_source", label: "Point source" },
]

const ACOUSTIC_SOURCE_TYPE_DESCRIPTIONS: Record<AcousticSourceType, string> = {
  ambient: "Mixed evenly into the call, like room tone or traffic.",
  point_source:
    "Comes from a specific spot in the room, like a doorbell or a barking dog.",
}

/** One-line explanation of a sound for its option card. */
export function getBackgroundSoundDescription(
  sound: BackgroundSoundResource,
): string | undefined {
  if (sound.value === "off") {
    return "No background noise on the call."
  }
  if (sound.value === "backchanneling") {
    return BACKCHANNELING_EXPLANATION
  }
  const acousticSourceType = getBackgroundSoundAcousticSourceType(sound)
  return acousticSourceType
    ? ACOUSTIC_SOURCE_TYPE_DESCRIPTIONS[acousticSourceType]
    : undefined
}

export function getBackgroundSoundAcousticSourceType(
  sound: BackgroundSoundResource,
): AcousticSourceType | undefined {
  if (sound.value === "off") {
    return undefined
  }
  return (
    (sound as BackgroundSoundWithAcousticSource).acoustic_source_type ??
    "ambient"
  )
}

export function AcousticSourceTypeTag({ type }: { type: AcousticSourceType }) {
  return (
    <Badge className="h-5 px-1.5 text-[10px]">
      {ACOUSTIC_SOURCE_TYPE_OPTIONS.find((option) => option.value === type)
        ?.label ?? "Ambient"}
    </Badge>
  )
}

function AcousticSourceTypeTooltipContent() {
  return (
    <div className="max-w-xs space-y-1.5">
      <p>
        <span className="font-medium">Ambient</span> — mixed evenly into the
        call with no particular origin. Use it for diffuse sound like room tone,
        rain, or traffic.
      </p>
      <p>
        <span className="font-medium">Point source</span> — rendered as if it
        comes from a specific spot in the same room as the speaker. Use it for
        sound with an obvious origin, like a doorbell, a barking dog, or a
        crying baby.
      </p>
      <p className="text-coval-text-muted">
        Point source only changes the audio when Situate Speaker is set. Without
        a preset it is mixed the same as ambient.
      </p>
    </div>
  )
}

function SoundGroupHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="pt-1 text-xs font-medium text-coval-text-muted">{children}</p>
  )
}

function BackgroundSoundVolume({
  controller,
}: {
  controller: PersonaFormController
}) {
  const {
    currentlyPlayingAudioId,
    formData,
    isSpeechDisabled,
    selectedBackgroundSound,
    setCurrentlyPlayingAudioId,
    updateMetadata,
  } = controller
  const defaultVolume = selectedBackgroundSound?.default_volume ?? 0.3
  const storedVolume = (formData.metadata as VoicePersonaConfig)
    .background_sound_volume
  const sliderPosition =
    storedVolume !== undefined && storedVolume !== null
      ? Math.max(0, Math.min(1, storedVolume / defaultVolume - 0.5))
      : 0.5
  const effectiveVolume = Math.max(0.1, defaultVolume * (0.5 + sliderPosition))
  const multiplierPercent = Math.round((0.5 + sliderPosition) * 100)

  return (
    <div className="flex items-center gap-3 rounded-md border border-coval-border px-3 py-2">
      <span className="w-14 shrink-0 text-xs font-medium text-coval-text-muted">
        Volume
      </span>
      <AudioPreviewButton
        previewUrl={selectedBackgroundSound?.preview_url ?? undefined}
        audioLabel={selectedBackgroundSound?.display_name || "Background sound"}
        volume={effectiveVolume}
        currentlyPlayingId={currentlyPlayingAudioId}
        onPlayingChange={setCurrentlyPlayingAudioId}
      />
      <Slider
        aria-label="Background sound volume adjustment"
        step={0.05}
        min={0}
        max={1}
        value={[sliderPosition]}
        onValueChange={(value) => {
          const multiplier = 0.5 + value[0]
          updateMetadata({
            background_sound_volume: Math.max(0.1, defaultVolume * multiplier),
          })
        }}
        disabled={isSpeechDisabled}
        className="flex-1"
      />
      <span className="w-12 shrink-0 text-right text-xs font-medium tabular-nums">
        {multiplierPercent}%
      </span>
    </div>
  )
}

/**
 * Picks the persona's background noise: built-in sounds, the workspace's
 * uploads, and, if one is selected, an archived sound. Uploading, renaming,
 * and archiving custom sounds live here too, so the settings row stays quiet.
 */
export function PersonaBackgroundNoiseDialog({
  controller,
  isOpen,
  onClose,
}: {
  controller: PersonaFormController
  isOpen: boolean
  onClose: () => void
}) {
  const sourceTypeLabelId = React.useId()
  const {
    activeBuiltInBackgroundSounds,
    activeCustomBackgroundSounds,
    backgroundSoundUploadAcousticSourceType,
    backgroundSoundUploadFile,
    backgroundSoundUploadName,
    currentlyPlayingAudioId,
    customSoundNameDraft,
    handleArchiveBackgroundSound,
    handleBackgroundSoundUpload,
    handleRenameBackgroundSound,
    isBackgroundSoundUpdating,
    isBackgroundSoundUploadOpen,
    isBackgroundSoundUploading,
    isSpeechDisabled,
    portableAssetsOnly,
    selectedArchivedBackgroundSound,
    selectedBackgroundSoundValue,
    selectedCustomBackgroundSound,
    setBackgroundSoundUploadAcousticSourceType,
    setBackgroundSoundUploadFile,
    setBackgroundSoundUploadName,
    setCurrentlyPlayingAudioId,
    setCustomSoundNameDraft,
    setIsBackgroundSoundUploadOpen,
    updateMetadata,
  } = controller

  const selectSound = (value: string) => {
    setCurrentlyPlayingAudioId(null)
    updateMetadata({
      background_sound: value,
      background_sound_volume: undefined,
    })
  }

  const renderSound = (sound: BackgroundSoundResource, suffix?: string) => {
    const acousticSourceType = getBackgroundSoundAcousticSourceType(sound)
    return (
      <OptionCard
        key={sound.value}
        label={suffix ? `${sound.display_name} ${suffix}` : sound.display_name}
        description={getBackgroundSoundDescription(sound)}
        badge={
          acousticSourceType ? (
            <AcousticSourceTypeTag type={acousticSourceType} />
          ) : undefined
        }
        previewUrl={sound.preview_url ?? undefined}
        previewVolume={sound.default_volume ?? undefined}
        isSelected={selectedBackgroundSoundValue === sound.value}
        isDisabled={isSpeechDisabled}
        currentlyPlayingAudioId={currentlyPlayingAudioId}
        onPlayingChange={setCurrentlyPlayingAudioId}
        onSelect={() => selectSound(sound.value)}
      />
    )
  }

  const isCustomSoundSaveDisabled =
    !selectedCustomBackgroundSound ||
    customSoundNameDraft.trim() === selectedCustomBackgroundSound.display_name

  const resetUploadDraft = () => {
    setBackgroundSoundUploadFile(null)
    setBackgroundSoundUploadName("")
    setBackgroundSoundUploadAcousticSourceType(undefined)
  }

  const closeUploadView = () => {
    setIsBackgroundSoundUploadOpen(false)
    resetUploadDraft()
  }

  const handleClose = () => {
    setCurrentlyPlayingAudioId(null)
    if (isBackgroundSoundUploadOpen) {
      closeUploadView()
    }
    onClose()
  }

  // One Dialog root for both views. Only the header, body, and footer swap, so
  // moving between the list and the upload form never re-runs the overlay and
  // open animation. The keyed body fades the new view in.
  const uploadView = (
    <>
      <DialogHeader>
        <DialogTitle>Upload Background Sound</DialogTitle>
      </DialogHeader>
      <DialogBody
        key="upload"
        className="animate-in fade-in-0 space-y-4 duration-150"
      >
        <FormField label="Name">
          {(ids) => (
            <Input
              {...ids}
              aria-label="Background sound display name"
              placeholder="e.g. Busy café"
              value={backgroundSoundUploadName}
              disabled={isBackgroundSoundUploading}
              onChange={(event) =>
                setBackgroundSoundUploadName(event.target.value)
              }
            />
          )}
        </FormField>
        <FormField label="Audio file">
          <FileDropZone
            file={backgroundSoundUploadFile}
            accept={BACKGROUND_SOUND_UPLOAD_ACCEPT}
            hint="WAV or MP3"
            label="Background sound audio file"
            fileIcon={<FileAudio />}
            disabled={isBackgroundSoundUploading}
            onFileChange={(file) => {
              setBackgroundSoundUploadFile(file)
              if (file && !backgroundSoundUploadName.trim()) {
                setBackgroundSoundUploadName(file.name.replace(/\.[^.]+$/, ""))
              }
            }}
          />
        </FormField>
        <div className="flex items-center justify-between gap-6">
          <GroupLabel
            id={sourceTypeLabelId}
            tooltip={<AcousticSourceTypeTooltipContent />}
          >
            Source type
          </GroupLabel>
          <Tabs
            value={
              backgroundSoundUploadAcousticSourceType ??
              ACOUSTIC_SOURCE_TYPE_UNSET_VALUE
            }
            onValueChange={(selected) =>
              setBackgroundSoundUploadAcousticSourceType(
                selected === ACOUSTIC_SOURCE_TYPE_UNSET_VALUE
                  ? undefined
                  : (selected as AcousticSourceType),
              )
            }
          >
            <TabsList variant="pill" aria-labelledby={sourceTypeLabelId}>
              <TabsTrigger
                value={ACOUSTIC_SOURCE_TYPE_UNSET_VALUE}
                variant="pill"
                className="px-3"
                disabled={isBackgroundSoundUploading}
              >
                None
              </TabsTrigger>
              {ACOUSTIC_SOURCE_TYPE_OPTIONS.map((option) => (
                <TabsTrigger
                  key={option.value}
                  value={option.value}
                  variant="pill"
                  className="px-3"
                  disabled={isBackgroundSoundUploading}
                >
                  {option.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={closeUploadView}
          disabled={isBackgroundSoundUploading}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => void handleBackgroundSoundUpload()}
          disabled={!backgroundSoundUploadFile || isBackgroundSoundUploading}
        >
          {isBackgroundSoundUploading ? "Uploading…" : "Upload"}
        </Button>
      </DialogFooter>
    </>
  )

  const listView = (
    <>
      <DialogHeader>
        <DialogTitle>Background Noise</DialogTitle>
        <p className="text-xs text-coval-text-muted">
          Ambient sound mixed into the persona&apos;s side of the call.
        </p>
      </DialogHeader>
      <DialogBody
        key="list"
        className="animate-in fade-in-0 space-y-3 duration-150"
      >
        <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
          <SoundGroupHeading>Built-in</SoundGroupHeading>
          {activeBuiltInBackgroundSounds.map((sound) => renderSound(sound))}
          {activeCustomBackgroundSounds.length > 0 && (
            <>
              <SoundGroupHeading>Your uploads</SoundGroupHeading>
              {activeCustomBackgroundSounds.map((sound) => renderSound(sound))}
            </>
          )}
          {selectedArchivedBackgroundSound && (
            <>
              <SoundGroupHeading>Archived</SoundGroupHeading>
              {renderSound(selectedArchivedBackgroundSound, "(archived)")}
            </>
          )}
        </div>
        {selectedBackgroundSoundValue !== "off" && (
          <BackgroundSoundVolume controller={controller} />
        )}
        {!portableAssetsOnly && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              className="h-8 px-2 text-xs"
              onClick={() => setIsBackgroundSoundUploadOpen(true)}
              disabled={isSpeechDisabled || isBackgroundSoundUploading}
            >
              <Upload className="mr-1.5 h-3.5 w-3.5" />
              Upload
            </Button>
            {selectedCustomBackgroundSound && (
              <>
                <Input
                  aria-label="Custom background sound name"
                  value={customSoundNameDraft}
                  onChange={(event) =>
                    setCustomSoundNameDraft(event.target.value)
                  }
                  disabled={isSpeechDisabled || isBackgroundSoundUpdating}
                  className="h-8 min-w-[180px] flex-1"
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="h-8 px-2 text-xs"
                  onClick={() => void handleRenameBackgroundSound()}
                  disabled={
                    isSpeechDisabled ||
                    isBackgroundSoundUpdating ||
                    isCustomSoundSaveDisabled
                  }
                >
                  <Check className="mr-1.5 h-3.5 w-3.5" />
                  Save
                </Button>
                {selectedCustomBackgroundSound.status === "archived" ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-8 px-2 text-xs"
                    onClick={() => void handleArchiveBackgroundSound("active")}
                    disabled={isSpeechDisabled || isBackgroundSoundUpdating}
                  >
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                    Restore
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-8 px-2 text-xs"
                    onClick={() =>
                      void handleArchiveBackgroundSound("archived")
                    }
                    disabled={isSpeechDisabled || isBackgroundSoundUpdating}
                  >
                    <Archive className="mr-1.5 h-3.5 w-3.5" />
                    Archive
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button type="button" onClick={handleClose}>
          Done
        </Button>
      </DialogFooter>
    </>
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="w-full max-w-xl">
        {isBackgroundSoundUploadOpen && !portableAssetsOnly
          ? uploadView
          : listView}
      </DialogContent>
    </Dialog>
  )
}
